'use client'

import Image from 'next/image'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CategoryFilterNav } from '@/components/CategoryFilterNav'
import { BlogArticleCard, getBlogArticleCardProps } from '@/components/Blog/BlogArticleCard'
import { BlogArticleCardSkeleton } from '@/components/Blog/BlogArticleCardSkeleton'
import { BlogPostsPagination } from '@/components/Blog/BlogPostsPagination'
import { BlogPostsSearchPill } from '@/components/Blog/BlogPostsSearchPill'
import { containerClassName } from '@/components/Container'
import { Button } from '@/components/ui/button'
import type { BlogCategory } from '@/lib/blog/getBlogCategories'
import { buildPostsQueryString, POSTS_PAGE_LIMIT, type PostsPageResponse } from '@/lib/blog/postsQuery'
import type { BlogPostData } from '@/lib/blog/postCard'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { useTemplatePathMap } from '@/lib/pages/LocalizedPagePathsProvider'
import { getArchiveItemHrefFromMap } from '@/lib/pages/pagePaths'
import type { Category } from '@/payload-types'
import { useDebounce } from '@/utilities/useDebounce'
import { cn } from '@/utilities/ui'

const GRID_SECTION_BACKGROUND = '/media/homepage/blog/grid-section-background.png'

const MOBILE_SCROLL_EXTRA_OFFSET = 8
const DESKTOP_SCROLL_EXTRA_OFFSET = -48

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
}

function getScrollSafeTop() {
  const headerBottom = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--mobile-header-bottom'),
  )

  const base = Number.isFinite(headerBottom) ? headerBottom : 80
  const extraOffset = isMobileViewport() ? MOBILE_SCROLL_EXTRA_OFFSET : DESKTOP_SCROLL_EXTRA_OFFSET

  return base + extraOffset
}

function scrollArticlesSectionToTop(section: HTMLElement | null) {
  if (!section) return

  const top = section.getBoundingClientRect().top + window.scrollY - getScrollSafeTop()

  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

function postMatchesSearch(post: BlogPostData, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true

  const title = post.title?.toLowerCase() ?? ''
  const shortDescription = post.shortDescription?.toLowerCase() ?? ''

  return title.includes(normalizedQuery) || shortDescription.includes(normalizedQuery)
}

function getPostCategoryTitles(categories: BlogPostData['categories']): string[] {
  if (!categories || !Array.isArray(categories)) return []

  return categories
    .map((category) => {
      if (typeof category === 'object' && category !== null) {
        return (category as Category).title ?? undefined
      }
      return undefined
    })
    .filter((title): title is string => Boolean(title))
}

type BlogArticlesGridProps = {
  categories: BlogCategory[]
  className?: string
  excludePostId?: number | string
  headingId?: string
  initialPage?: number
  initialTotalPages?: number
  posts: BlogPostData[]
}

export const BlogArticlesGrid: React.FC<BlogArticlesGridProps> = ({
  categories,
  className,
  excludePostId,
  headingId,
  initialPage = 1,
  initialTotalPages = 1,
  posts: initialPosts,
}) => {
  const locale = useLocale()
  const templatePaths = useTemplatePathMap()
  const messages = useMessages()
  const copy = messages.landing.blog
  const isServerPaginated = excludePostId != null

  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const [posts, setPosts] = useState(initialPosts)
  const [page, setPage] = useState(initialPage)
  const [totalPages, setTotalPages] = useState(initialTotalPages)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [liveRegionMessage, setLiveRegionMessage] = useState('')

  const fetchAbortRef = useRef<AbortController | null>(null)
  const failedPageRef = useRef<number | null>(null)
  const isFirstSearchEffectRef = useRef(true)
  const activeCategoryRef = useRef(activeCategory)
  const sectionRef = useRef<HTMLElement | null>(null)

  activeCategoryRef.current = activeCategory

  const categoryTitles = useMemo(() => categories.map((category) => category.title), [categories])
  const categoryOrder = useMemo(() => ['all', ...categoryTitles], [categoryTitles])
  const categoryIdByTitle = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.title, category.id])),
    [categories],
  )
  const colorByTitle = useMemo(
    () =>
      Object.fromEntries(
        categories
          .filter((category) => category.color)
          .map((category) => [category.title, category.color as string]),
      ),
    [categories],
  )

  const categoryLabels = useMemo(
    () =>
      Object.fromEntries([
        ['all', copy.categoryAll],
        ...categoryTitles.map((category) => [category, category]),
      ]) as Record<string, string>,
    [categoryTitles, copy.categoryAll],
  )

  const resolveCategoryId = useCallback(
    (category: string) => (category === 'all' ? null : (categoryIdByTitle[category] ?? null)),
    [categoryIdByTitle],
  )

  const fetchPosts = useCallback(
    async ({
      announcePage = false,
      category,
      nextPage,
      q,
      signal,
    }: {
      announcePage?: boolean
      category: string
      nextPage: number
      q?: string
      signal?: AbortSignal
    }) => {
      const query = buildPostsQueryString({
        categoryId: resolveCategoryId(category),
        excludeId: excludePostId,
        locale,
        page: nextPage,
        q,
      })

      const response = await fetch(`/api/posts?${query}`, {
        cache: 'no-store',
        signal,
      })

      if (!response.ok) {
        throw new Error('Failed to fetch posts')
      }

      const data = (await response.json()) as PostsPageResponse<BlogPostData>

      if (signal?.aborted) {
        return null
      }

      const resolvedPage = Number(data.page ?? nextPage)
      const resolvedTotalPages = Number(data.totalPages ?? 1)

      setPosts(data.docs)
      setPage(resolvedPage)
      setTotalPages(resolvedTotalPages)

      if (announcePage) {
        setLiveRegionMessage(
          copy.showingPage
            .replace('{page}', String(resolvedPage))
            .replace('{totalPages}', String(resolvedTotalPages)),
        )
      }

      return data
    },
    [copy.showingPage, excludePostId, locale, resolveCategoryId],
  )

  const goToPage = useCallback(
    async (pageNumber: number, { shouldScroll = false }: { shouldScroll?: boolean } = {}) => {
      if (!isServerPaginated || isLoadingMore) return
      if (pageNumber < 1 || pageNumber > totalPages) return
      if (pageNumber === page && !loadError) return

      fetchAbortRef.current?.abort()
      const controller = new AbortController()
      fetchAbortRef.current = controller

      setLoadError(null)
      setIsLoadingMore(true)

      try {
        const data = await fetchPosts({
          announcePage: shouldScroll,
          category: activeCategory,
          nextPage: pageNumber,
          q: debouncedSearchQuery,
          signal: controller.signal,
        })

        if (!data || controller.signal.aborted) {
          return
        }

        failedPageRef.current = null

        if (shouldScroll) {
          scrollArticlesSectionToTop(sectionRef.current)
        }
      } catch {
        if (controller.signal.aborted) return
        failedPageRef.current = pageNumber
        setLoadError(copy.loadMoreError)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingMore(false)
        }
      }
    },
    [
      activeCategory,
      copy.loadMoreError,
      debouncedSearchQuery,
      fetchPosts,
      isLoadingMore,
      isServerPaginated,
      loadError,
      page,
      totalPages,
    ],
  )

  const handleCategoryChange = useCallback(
    async (category: string) => {
      setActiveCategory(category)
      setLoadError(null)

      if (!isServerPaginated) {
        return
      }

      fetchAbortRef.current?.abort()
      const controller = new AbortController()
      fetchAbortRef.current = controller

      setPosts([])
      setIsLoadingMore(true)

      try {
        await fetchPosts({
          category,
          nextPage: 1,
          q: debouncedSearchQuery,
          signal: controller.signal,
        })
        failedPageRef.current = null
      } catch {
        if (controller.signal.aborted) return
        failedPageRef.current = 1
        setLoadError(copy.loadMoreError)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingMore(false)
        }
      }
    },
    [copy.loadMoreError, debouncedSearchQuery, fetchPosts, isServerPaginated],
  )

  useEffect(() => {
    if (!isServerPaginated) return

    if (isFirstSearchEffectRef.current) {
      isFirstSearchEffectRef.current = false
      return
    }

    fetchAbortRef.current?.abort()
    const controller = new AbortController()
    fetchAbortRef.current = controller

    setLoadError(null)
    setPosts([])
    setIsLoadingMore(true)

    void fetchPosts({
      category: activeCategoryRef.current,
      nextPage: 1,
      q: debouncedSearchQuery,
      signal: controller.signal,
    })
      .then(() => {
        if (controller.signal.aborted) return
        failedPageRef.current = null
      })
      .catch(() => {
        if (controller.signal.aborted) return
        failedPageRef.current = 1
        setLoadError(copy.loadMoreError)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingMore(false)
        }
      })
  }, [copy.loadMoreError, debouncedSearchQuery, fetchPosts, isServerPaginated])

  const handleRetry = useCallback(async () => {
    if (!isServerPaginated || isLoadingMore) return

    const retryPage = failedPageRef.current ?? page
    await goToPage(retryPage, { shouldScroll: false })
  }, [goToPage, isLoadingMore, isServerPaginated, page])

  const handlePrevious = useCallback(() => {
    void goToPage(page - 1, { shouldScroll: true })
  }, [goToPage, page])

  const handleNext = useCallback(() => {
    void goToPage(page + 1, { shouldScroll: true })
  }, [goToPage, page])

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      void goToPage(pageNumber, { shouldScroll: true })
    },
    [goToPage],
  )

  useEffect(() => {
    return () => {
      fetchAbortRef.current?.abort()
    }
  }, [])

  const visiblePosts = useMemo(() => {
    if (isServerPaginated) return posts

    let filtered = posts

    if (activeCategory !== 'all') {
      filtered = filtered.filter((post) =>
        getPostCategoryTitles(post.categories).some((title) => title === activeCategory),
      )
    }

    if (debouncedSearchQuery.trim()) {
      filtered = filtered.filter((post) => postMatchesSearch(post, debouncedSearchQuery))
    }

    return filtered
  }, [activeCategory, debouncedSearchQuery, isServerPaginated, posts])

  if (initialPosts.length === 0 && categories.length === 0 && !isServerPaginated) {
    return null
  }

  const isSearchPending = searchQuery.trim() !== debouncedSearchQuery.trim()
  const showSkeleton = isSearchPending || (isServerPaginated && isLoadingMore)
  const skeletonCount = POSTS_PAGE_LIMIT
  const hasActiveFilters = activeCategory !== 'all' || debouncedSearchQuery.trim().length > 0
  const showNoResults = !showSkeleton && !loadError && visiblePosts.length === 0 && hasActiveFilters
  const statusMessage = showSkeleton
    ? copy.loadingMore
    : showNoResults
      ? messages.search.noResults
      : liveRegionMessage

  const inner = (
    <>
      {headingId ? (
        <h2 className="sr-only" id={headingId}>
          {copy.articlesSectionHeading}
        </h2>
      ) : null}

      <p aria-live="polite" className="sr-only">
        {statusMessage}
      </p>

      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {categories.length > 0 ? (
          <CategoryFilterNav
            activeCategory={activeCategory}
            ariaLabel={copy.categoryFiltersAriaLabel}
            categoryLabels={categoryLabels}
            categoryOrder={categoryOrder}
            desktopAlign="start"
            desktopWidth="fit"
            layoutId="blog-category-indicator"
            onSelect={handleCategoryChange}
          />
        ) : null}

        <BlogPostsSearchPill
          label={messages.search.title}
          onChange={setSearchQuery}
          placeholder={messages.search.placeholder}
          value={searchQuery}
        />
      </div>

      {showSkeleton ? (
        <ul
          aria-busy
          className="grid w-full gap-x-2 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-2 lg:gap-y-6"
        >
          {Array.from({ length: skeletonCount }, (_, index) => (
            <BlogArticleCardSkeleton key={`blog-card-skeleton-${index}`} />
          ))}
        </ul>
      ) : showNoResults ? (
        <p className="w-full font-outfit text-base text-dark-800/80">{messages.search.noResults}</p>
      ) : (
        <ul className="grid w-full gap-x-2 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-2 lg:gap-y-6">
          {visiblePosts.map((post, index) => (
            <BlogArticleCard
              key={post.id}
              {...getBlogArticleCardProps(
                post,
                getArchiveItemHrefFromMap('posts', post.slug, locale, templatePaths),
                copy.readTime,
                { colorByTitle, index },
              )}
            />
          ))}
        </ul>
      )}

      {loadError ? (
        <div className="flex flex-col items-center gap-3">
          <p className="font-outfit text-base text-dark-800/80" role="alert">
            {copy.loadMoreError}
          </p>
          <Button
            className="h-12 border-black/30 bg-white/30 px-6 hover:bg-white/50"
            disabled={isLoadingMore}
            onClick={handleRetry}
            type="button"
            variant="outline"
          >
            {isLoadingMore ? copy.loadingMore : copy.loadMoreRetry}
          </Button>
        </div>
      ) : null}

      {isServerPaginated && totalPages > 1 ? (
        <BlogPostsPagination
          currentPage={page}
          isLoading={isLoadingMore}
          nextLabel={copy.paginationNext}
          onNext={handleNext}
          onPageChange={handlePageChange}
          onPrevious={handlePrevious}
          paginationAriaLabel={copy.paginationAriaLabel}
          previousLabel={copy.paginationPrevious}
          totalPages={totalPages}
        />
      ) : null}
    </>
  )

  const wrapperClassName = cn('relative isolate overflow-hidden bg-cream', className)

  if (headingId) {
    return (
      <section
        ref={sectionRef}
        aria-labelledby={headingId}
        className={wrapperClassName}
      >
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          <div className="absolute left-0 top-[-6%] h-[148%] w-full">
            <Image
              alt=""
              className="object-cover mix-blend-lighten opacity-80"
              fill
              sizes="100vw"
              src={GRID_SECTION_BACKGROUND}
            />
          </div>
        </div>

        <div className={cn(containerClassName, 'relative z-10 flex w-full flex-col items-center gap-10 py-12 lg:gap-10 lg:py-20')}>
          {inner}
        </div>
      </section>
    )
  }

  return (
    <div ref={sectionRef as React.RefObject<HTMLDivElement>} className={wrapperClassName}>
      <div aria-hidden className="absolute inset-0 overflow-hidden">
        <div className="absolute left-0 top-[-6%] h-[148%] w-full">
          <Image
            alt=""
            className="object-cover mix-blend-lighten opacity-80"
            fill
            sizes="100vw"
            src={GRID_SECTION_BACKGROUND}
          />
        </div>
      </div>

      <div className={cn(containerClassName, 'relative z-10 flex w-full flex-col items-center gap-10 py-12 lg:gap-10 lg:py-20')}>
        {inner}
      </div>
    </div>
  )
}
